import type {
  BuildLessonAccessOptions,
  CourseLessonAccessState,
  CourseLessonProgressSnapshot,
  CourseProgressLesson,
  CourseProgressModule,
} from './course-progress';

export const CourseModuleDripRule = {
  INHERIT: 'inherit',
  NONE: 'none',
  DAYS_AFTER_ENROLL: 'days_after_enroll',
  CALENDAR_DATE: 'calendar_date',
} as const;

export type CourseModuleDripRuleValue =
  (typeof CourseModuleDripRule)[keyof typeof CourseModuleDripRule];

export type CourseModuleDripConfig = {
  id: string;
  title?: string;
  dripRule?: string | null;
  dripDaysAfterEnroll?: number | null;
  dripUnlockAt?: Date | string | null;
};

export type CourseProgressModuleWithDrip = CourseProgressModule & CourseModuleDripConfig;

export function moduleHasDripSchedule(module: CourseModuleDripConfig): boolean {
  const rule = resolveEffectiveDripRule(module.dripRule);
  return rule === CourseModuleDripRule.DAYS_AFTER_ENROLL || rule === CourseModuleDripRule.CALENDAR_DATE;
}

export function resolveEffectiveDripRule(rule?: string | null): CourseModuleDripRuleValue {
  if (!rule || rule === CourseModuleDripRule.INHERIT) return CourseModuleDripRule.NONE;
  if (
    rule === CourseModuleDripRule.NONE ||
    rule === CourseModuleDripRule.DAYS_AFTER_ENROLL ||
    rule === CourseModuleDripRule.CALENDAR_DATE
  ) {
    return rule;
  }
  return CourseModuleDripRule.NONE;
}

export function resolveModuleDripUnlockAt(
  enrolledAt: Date,
  module: Pick<CourseModuleDripConfig, 'dripRule' | 'dripDaysAfterEnroll' | 'dripUnlockAt'>,
): Date {
  const rule = resolveEffectiveDripRule(module.dripRule);
  if (rule === CourseModuleDripRule.NONE) return enrolledAt;
  if (rule === CourseModuleDripRule.DAYS_AFTER_ENROLL) {
    const days = module.dripDaysAfterEnroll ?? 0;
    if (days <= 0) return enrolledAt;
    return new Date(enrolledAt.getTime() + days * 24 * 60 * 60 * 1000);
  }
  if (rule === CourseModuleDripRule.CALENDAR_DATE) {
    if (!module.dripUnlockAt) return enrolledAt;
    return module.dripUnlockAt instanceof Date
      ? module.dripUnlockAt
      : new Date(module.dripUnlockAt);
  }
  return enrolledAt;
}

export function isModuleDripUnlocked(
  enrolledAt: Date | null | undefined,
  module: CourseModuleDripConfig,
  now: Date = new Date(),
): boolean {
  if (!enrolledAt) return false;
  return resolveModuleDripUnlockAt(enrolledAt, module).getTime() <= now.getTime();
}

export function listNewlyUnlockedDripModules(params: {
  enrolledAt: Date;
  modules: CourseModuleDripConfig[];
  alreadyNotifiedModuleIds: string[];
  now?: Date;
}): Array<{ moduleId: string; moduleTitle: string; unlockAt: Date }> {
  const now = params.now ?? new Date();
  const notified = new Set(params.alreadyNotifiedModuleIds);
  const unlocked: Array<{ moduleId: string; moduleTitle: string; unlockAt: Date }> = [];

  for (const module of params.modules) {
    if (!moduleHasDripSchedule(module)) continue;
    if (notified.has(module.id)) continue;
    const unlockAt = resolveModuleDripUnlockAt(params.enrolledAt, module);
    if (unlockAt.getTime() > now.getTime()) continue;
    unlocked.push({
      moduleId: module.id,
      moduleTitle: module.title?.trim() || 'New module',
      unlockAt,
    });
  }

  return unlocked;
}

export function buildLessonAccessStatesFromModules(
  modules: CourseProgressModuleWithDrip[],
  progressByLesson: Record<string, CourseLessonProgressSnapshot>,
  entitled: boolean,
  enrolledAt?: Date | null,
  now?: Date,
  options?: BuildLessonAccessOptions,
): Record<string, CourseLessonAccessState> {
  const states: Record<string, CourseLessonAccessState> = {};
  let previousCompleted = true;
  const currentNow = now ?? new Date();

  for (const module of modules) {
    const moduleUnlocked =
      entitled && isModuleDripUnlocked(enrolledAt ?? null, module, currentNow);
    const unlockAt =
      entitled && enrolledAt ? resolveModuleDripUnlockAt(enrolledAt, module) : null;
    const unlockAtIso =
      unlockAt && unlockAt.getTime() > currentNow.getTime() ? unlockAt.toISOString() : null;

    for (const lesson of module.lessons ?? []) {
      const snapshot = progressByLesson[lesson.id];
      const completed = snapshot?.completed ?? false;
      const lastPositionSeconds = snapshot?.lastPositionSeconds ?? null;

      if (lesson.freePreview && !options?.respectLocksForFreePreview) {
        states[lesson.id] = {
          accessible: true,
          locked: false,
          dripLocked: false,
          missingPrerequisiteLessonIds: [],
          unlockAt: null,
          completed: entitled ? completed : false,
          lastPositionSeconds: entitled ? lastPositionSeconds : null,
        };
        if (entitled) previousCompleted = completed;
        continue;
      }

      if (!entitled) {
        states[lesson.id] = {
          accessible: false,
          locked: false,
          dripLocked: false,
          missingPrerequisiteLessonIds: [],
          unlockAt: null,
          completed: false,
          lastPositionSeconds: null,
        };
        continue;
      }

      if (!moduleUnlocked) {
        states[lesson.id] = {
          accessible: false,
          locked: true,
          dripLocked: true,
          missingPrerequisiteLessonIds: [],
          unlockAt: unlockAtIso,
          completed: false,
          lastPositionSeconds: null,
        };
        continue;
      }

      const prerequisiteLessonIds = Array.isArray(lesson.prerequisiteLessonIds)
        ? lesson.prerequisiteLessonIds.filter((id): id is string => typeof id === 'string')
        : [];
      const missingPrerequisiteLessonIds = prerequisiteLessonIds.filter(
        (prerequisiteLessonId) => !progressByLesson[prerequisiteLessonId]?.completed,
      );
      const blockedByLock = Boolean(lesson.lockUntilPreviousComplete) && !previousCompleted;
      const blockedByPrerequisites = missingPrerequisiteLessonIds.length > 0;
      states[lesson.id] = {
        accessible: !blockedByLock && !blockedByPrerequisites,
        locked: blockedByLock || blockedByPrerequisites,
        dripLocked: false,
        missingPrerequisiteLessonIds,
        unlockAt: null,
        completed,
        lastPositionSeconds,
      };
      previousCompleted = completed;
    }
  }

  return states;
}

export function flattenPublishedLessonsFromModules(
  modules: CourseProgressModule[],
): CourseProgressLesson[] {
  return modules.flatMap((module) => module.lessons ?? []);
}
